import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown, Form } from 'semantic-ui-react';
import { Popup } from '../../lib/custom-ui';

import { useForm } from '../../hooks';

import styles from './ListMoveStep.module.scss';

const ListMoveStep = React.memo(
  ({ projectsToLists, defaultPath, onMove, onTransfer, onBoardFetch, onBack, onClose }) => {
    const [t] = useTranslation();

    const [path, handleFieldChange] = useForm(() => ({
      projectId: null,
      boardId: null,
      ...defaultPath,
    }));

    const selectedProject = useMemo(() => {
      const project = projectsToLists.find((p) => p.id === path.projectId) || null;
      if (project) {
        // eslint-disable-next-line no-console
        console.log('[ListMoveStep] selected project:', {
          id: project.id,
          name: project.name,
          boardsCount: project.boards ? project.boards.length : 0,
        });
      }
      return project;
    }, [projectsToLists, path.projectId]);

    const selectedBoard = useMemo(
      () =>
        (selectedProject && selectedProject.boards.find((board) => board.id === path.boardId)) ||
        null,
      [selectedProject, path.boardId],
    );

    const handleBoardIdChange = useCallback(
      (event, data) => {
        const targetBoard = selectedProject.boards.find((board) => board.id === data.value);

        if (targetBoard && targetBoard.isFetching === null) {
          // eslint-disable-next-line no-console
          console.log('[ListMoveStep] fetchBoard for boardId:', data.value);
          onBoardFetch(data.value);
        } else {
          // eslint-disable-next-line no-console
          console.log('[ListMoveStep] skip fetchBoard, board already loaded or missing:', {
            boardId: data.value,
            targetBoard,
          });
        }

        handleFieldChange(event, data);
      },
      [onBoardFetch, handleFieldChange, selectedProject],
    );

    const handleSubmit = useCallback(() => {
      if (selectedBoard.id !== defaultPath.boardId) {
        onTransfer(selectedBoard.id);
        // onTransfer откроет шаг подтверждения, не закрываем попап здесь
      } else {
        // Если доска та же, просто закрываем
        onClose();
      }
    }, [defaultPath, onTransfer, onClose, selectedBoard]);

    return (
      <>
        <Popup.Header onBack={onBack}>
          {t('common.moveList', {
            context: 'title',
          })}
        </Popup.Header>
        <Popup.Content>
          <Form onSubmit={handleSubmit}>
            <div className={styles.text}>{t('common.project')}</div>
            <Dropdown
              fluid
              selection
              name="projectId"
              options={projectsToLists.map((project) => ({
                text: project.name,
                value: project.id,
              }))}
              value={selectedProject && selectedProject.id}
              placeholder={
                projectsToLists.length === 0 ? t('common.noProjects') : t('common.selectProject')
              }
              disabled={projectsToLists.length === 0}
              className={styles.field}
              onChange={handleFieldChange}
            />
            {selectedProject && (
              <>
                <div className={styles.text}>{t('common.board')}</div>
                <Dropdown
                  fluid
                  selection
                  name="boardId"
                  options={selectedProject.boards.map((board) => ({
                    text: board.name,
                    value: board.id,
                  }))}
                  value={selectedBoard && selectedBoard.id}
                  placeholder={
                    selectedProject.boards.length === 0
                      ? t('common.noBoards')
                      : t('common.selectBoard')
                  }
                  disabled={selectedProject.boards.length === 0}
                  className={styles.field}
                  onChange={handleBoardIdChange}
                />
              </>
            )}
            <Button
              positive
              content={t('action.move')}
              disabled={(selectedBoard && selectedBoard.isFetching !== false) || !selectedBoard}
            />
          </Form>
        </Popup.Content>
      </>
    );
  },
);

ListMoveStep.propTypes = {
  /* eslint-disable react/forbid-prop-types */
  projectsToLists: PropTypes.array.isRequired,
  defaultPath: PropTypes.object.isRequired,
  /* eslint-enable react/forbid-prop-types */
  onMove: PropTypes.func.isRequired,
  onTransfer: PropTypes.func.isRequired,
  onBoardFetch: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

ListMoveStep.defaultProps = {
  onBack: undefined,
};

export default ListMoveStep;
